import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import RichText from "@/components/RichText";
import { API } from "@/lib/api";

const DEFAULTS = {
  eyebrow: "",
  heading: "Prendre rendez-vous",
  subheading: "",
  open_time: "09:00",
  close_time: "18:00",
  slot_minutes: "30",
  closed_days: "0",
  currency: "€",
  success_message: "Votre demande de rendez-vous a bien été envoyée. Nous vous confirmerons rapidement.",
  variant: "light",
  anchor_id: "",
};

function toMinutes(t) {
  const [h, m] = String(t || "").split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function fmt(min) {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

function buildSlots(open, close, step) {
  const start = toMinutes(open);
  const end = toMinutes(close);
  const s = parseInt(step, 10) || 30;
  if (start === null || end === null || start >= end) return [];
  const out = [];
  for (let t = start; t + s <= end; t += s) out.push(fmt(t));
  return out;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function BookingSection({ props = {}, blocks, sectionId }) {
  const t = { ...DEFAULTS, ...props };
  const dark = t.variant === "dark";
  const services = (blocks === undefined ? [] : blocks).filter((b) => b.enabled !== false);

  const closedDays = useMemo(
    () => new Set(String(t.closed_days || "").split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n))),
    [t.closed_days]
  );
  const allSlots = useMemo(() => buildSlots(t.open_time, t.close_time, t.slot_minutes), [t.open_time, t.close_time, t.slot_minutes]);

  const [service, setService] = useState("");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [taken, setTaken] = useState([]);
  const [busy, setBusy] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [status, setStatus] = useState({ loading: false, done: false, error: "" });

  const chosenDay = date ? new Date(`${date}T00:00:00`).getDay() : null;
  const isClosed = chosenDay !== null && closedDays.has(chosenDay);

  useEffect(() => {
    if (!date || isClosed) { setTaken([]); setBusy([]); return; }
    let cancelled = false;
    axios
      .get(`${API}/booking/taken`, { params: { date, section_id: sectionId || "" } })
      .then((r) => { if (!cancelled) { setTaken(r.data?.taken || []); setBusy(r.data?.busy || []); } })
      .catch(() => { if (!cancelled) { setTaken([]); setBusy([]); } });
    return () => { cancelled = true; };
  }, [date, sectionId, isClosed]);

  const step = parseInt(t.slot_minutes, 10) || 30;
  const isBusy = (slot) => {
    const s = toMinutes(slot);
    return busy.some((b) => s < toMinutes(b.end) && s + step > toMinutes(b.start));
  };

  const nowMin = toMinutes(`${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`);
  const isPast = (slot) => date === todayISO() && toMinutes(slot) <= nowMin;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !time) {
      setStatus({ loading: false, done: false, error: "Merci de choisir un créneau et de renseigner votre nom et e-mail." });
      return;
    }
    setStatus({ loading: true, done: false, error: "" });
    try {
      await axios.post(`${API}/booking`, {
        section_id: sectionId || "",
        service,
        date,
        time,
        name: form.name,
        email: form.email,
        phone: form.phone,
        note: form.note,
      });
      setStatus({ loading: false, done: true, error: "" });
    } catch (err) {
      setStatus({ loading: false, done: false, error: err?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer." });
      if (err?.response?.status === 409) {
        axios.get(`${API}/booking/taken`, { params: { date, section_id: sectionId || "" } }).then((r) => { setTaken(r.data?.taken || []); setBusy(r.data?.busy || []); }).catch(() => {});
        setTime("");
      }
    }
  };

  const card = dark ? "bg-white/5 border-white/10" : "bg-white border-stone";
  const inputCls = `w-full rounded-xl border px-4 py-2.5 text-[15px] outline-none transition-colors focus:border-gold ${dark ? "bg-white/5 border-white/15 text-white placeholder-white/40" : "bg-cream border-stone text-ink placeholder-ash"}`;

  return (
    <section id={t.anchor_id || undefined} data-testid="booking-section" className={`scroll-mt-28 ${dark ? "bg-ink" : ""}`}>
      <div className="max-w-[1080px] mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-28">
        {(t.eyebrow || t.heading || t.subheading) && (
          <div className="text-center max-w-2xl mx-auto mb-12">
            {t.eyebrow && (
              <RichText as="span" className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold tracking-wide uppercase mb-4 ${dark ? "border-white/15 bg-white/5 text-gold" : "border-stone bg-sand text-gold"}`} html={t.eyebrow} />
            )}
            {t.heading && (
              <RichText as="h2" className={`font-display font-semibold text-[30px] md:text-[34px] leading-tight tracking-[-0.02em] ${dark ? "text-white" : "text-ink"}`} html={t.heading} />
            )}
            {t.subheading && (
              <RichText as="p" preserveParagraphs className={`text-[16px] mt-4 ${dark ? "text-white/60" : "text-graphite"}`} html={t.subheading} />
            )}
          </div>
        )}

        {status.done ? (
          <div data-testid="booking-success" className={`max-w-xl mx-auto text-center rounded-2xl border p-10 ${card}`}>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gold/15 text-gold text-2xl">✓</div>
            <p className={`text-[16px] ${dark ? "text-white/80" : "text-graphite"}`}>{t.success_message}</p>
            <div className={`mt-2 text-[14px] font-medium ${dark ? "text-white" : "text-ink"}`}>{date} · {time}{service ? ` · ${service}` : ""}</div>
          </div>
        ) : (
          <div className={`grid md:grid-cols-2 gap-8 rounded-2xl border p-6 md:p-8 ${card}`}>
            <div>
              {services.length > 0 && (
                <div className="mb-6">
                  <label className={`block text-[13px] font-semibold uppercase tracking-wide mb-3 ${dark ? "text-white/70" : "text-graphite"}`}>Prestation</label>
                  <div className="space-y-2">
                    {services.map((b, i) => {
                      const title = (b.props?.title || "").replace(/<[^>]+>/g, "");
                      const selected = service === title;
                      return (
                        <button
                          type="button"
                          key={b.id}
                          data-testid={`booking-service-${i}`}
                          onClick={() => setService(title)}
                          className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${selected ? "border-gold bg-gold/10" : dark ? "border-white/10 hover:border-white/25" : "border-stone hover:border-graphite"}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className={`font-medium text-[15px] ${dark ? "text-white" : "text-ink"}`}>{title || "Prestation"}</span>
                            {b.props?.price && <span className="text-gold font-semibold text-[14px] whitespace-nowrap">{b.props.price}{t.currency}</span>}
                          </div>
                          {(b.props?.desc || b.props?.duration) && (
                            <div className={`text-[13px] mt-0.5 ${dark ? "text-white/50" : "text-ash"}`}>{[b.props?.duration, b.props?.desc].filter(Boolean).join(" · ")}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <label className={`block text-[13px] font-semibold uppercase tracking-wide mb-2 ${dark ? "text-white/70" : "text-graphite"}`}>Date</label>
              <input
                type="date"
                data-testid="booking-date-input"
                min={todayISO()}
                value={date}
                onChange={(e) => { setDate(e.target.value); setTime(""); }}
                className={inputCls}
              />

              <label className={`block text-[13px] font-semibold uppercase tracking-wide mt-6 mb-2 ${dark ? "text-white/70" : "text-graphite"}`}>Créneau</label>
              {isClosed ? (
                <p className={`text-[14px] ${dark ? "text-white/50" : "text-ash"}`} data-testid="booking-closed">Fermé ce jour-là. Choisissez une autre date.</p>
              ) : allSlots.length === 0 ? (
                <p className={`text-[14px] ${dark ? "text-white/50" : "text-ash"}`}>Aucun créneau configuré.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {allSlots.map((slot) => {
                    const disabled = taken.includes(slot) || isPast(slot) || isBusy(slot);
                    const selected = time === slot;
                    return (
                      <button
                        type="button"
                        key={slot}
                        data-testid={`booking-slot-${slot}`}
                        disabled={disabled}
                        onClick={() => setTime(slot)}
                        className={`rounded-lg border py-2 text-[14px] font-medium transition-colors ${
                          selected
                            ? "border-gold bg-gold text-white"
                            : disabled
                            ? dark ? "border-white/5 text-white/20 line-through cursor-not-allowed" : "border-stone/60 text-ash line-through cursor-not-allowed"
                            : dark ? "border-white/15 text-white hover:border-gold" : "border-stone text-ink hover:border-gold"
                        }`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className={`block text-[13px] font-semibold uppercase tracking-wide mb-2 ${dark ? "text-white/70" : "text-graphite"}`}>Vos coordonnées</label>
                <input data-testid="booking-name-input" className={inputCls} placeholder="Nom complet" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <input data-testid="booking-email-input" type="email" className={inputCls} placeholder="Adresse e-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="booking-phone-input" className={inputCls} placeholder="Téléphone (optionnel)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea data-testid="booking-note-input" rows={3} className={inputCls} placeholder="Précisions (optionnel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              {status.error && <p data-testid="booking-error" className="text-[14px] text-red-500">{status.error}</p>}
              <button
                type="submit"
                data-testid="booking-submit-btn"
                disabled={status.loading}
                className="w-full rounded-full bg-gold px-6 py-3 text-white font-semibold text-[15px] transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              >
                {status.loading ? "Envoi..." : time ? `Réserver le ${date} à ${time}` : "Choisir un créneau"}
              </button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
